export const DESIGN_STORAGE_KEY = "konnectfbr_invoice_design_v1";
export const DESIGN_CANVAS_WIDTH = 560;
export const DESIGN_CANVAS_HEIGHT = 792;
export const PRINT_PAGE_WIDTH_MM = 194;
export const PRINT_PAGE_HEIGHT_MM = 277;

const clampDesignValue = (value, min, max) => Math.min(Math.max(value, min), max);

export const FBR_ITEM_FIELD_OPTIONS = [
  { key: "hsCode", label: "HS Code" },
  { key: "productDescription", label: "Description" },
  { key: "rate", label: "Rate of tax" },
  { key: "uoM", label: "UoM" },
  { key: "quantity", label: "Quantity" },
  { key: "totalValues", label: "Total Values" },
  { key: "valueSalesExcludingST", label: "Value excluding tax" },
  { key: "fixedNotifiedValueOrRetailPrice", label: "Fixed/Notified Value or Retail Price" },
  { key: "salesTaxApplicable", label: "Sales Tax" },
  { key: "salesTaxWithheldAtSource", label: "Sales Tax Withheld" },
  { key: "extraTax", label: "Extra Tax" },
  { key: "furtherTax", label: "Further Tax" },
  { key: "sroScheduleNo", label: "SRO Schedule No" },
  { key: "fedPayable", label: "FED Payable" },
  { key: "discount", label: "Discount" },
  { key: "saleType", label: "Sale Type" },
  { key: "sroItemSerialNo", label: "SRO Item Serial No" },
  { key: "validation", label: "Validation" },
  { key: "remarks", label: "Remarks" },
  { key: "invoiceNumber", label: "Invoice Number" },
];

export const DERIVED_ITEM_FIELD_OPTIONS = [
  { key: "sr", label: "Sr." },
  { key: "unitPrice", label: "Price per unit" },
  { key: "lineTotal", label: "Total Price" },
  { key: "totalValueIncludingSalesTax", label: "Total Value including sales tax" },
];

export const INVOICE_ITEM_FIELD_OPTIONS = [
  DERIVED_ITEM_FIELD_OPTIONS[0],
  ...FBR_ITEM_FIELD_OPTIONS,
  ...DERIVED_ITEM_FIELD_OPTIONS.slice(1),
];

export const DEFAULT_ITEM_COLUMNS = [
  "sr",
  "productDescription",
  "hsCode",
  "quantity",
  "unitPrice",
  "valueSalesExcludingST",
  "rate",
  "salesTaxApplicable",
  "totalValueIncludingSalesTax",
];

const DEFAULT_ITEM_COLUMN_ORDER = [
  ...DEFAULT_ITEM_COLUMNS,
  ...INVOICE_ITEM_FIELD_OPTIONS.map((field) => field.key).filter((key) => !DEFAULT_ITEM_COLUMNS.includes(key)),
];

export const DEFAULT_BREAKDOWN_ROWS = [
  { id: "salesBeforeDiscount", label: "Sales Amount (Excl. Tax) Before Discount", visible: true, valueType: "computed" },
  { id: "totalExcl", label: "Sales Amount (Excl. Tax)", visible: true, valueType: "computed" },
  { id: "discount", label: "Discount", visible: true, valueType: "computed" },
  { id: "totalTax", label: "Sales Tax", visible: true, valueType: "computed" },
  { id: "furtherTax", label: "Further Tax", visible: false, valueType: "computed" },
  { id: "extraTax", label: "Extra Tax", visible: false, valueType: "computed" },
  { id: "fedPayable", label: "FED Payable", visible: false, valueType: "computed" },
  { id: "salesTaxWithheldAtSource", label: "Sales Tax Withheld At Source", visible: false, valueType: "computed" },
  { id: "incomeTax236G236H", label: "236G / 236H", visible: true, valueType: "computed" },
  { id: "advanceIncomeTax", label: "Income Tax (Adv)", visible: true, valueType: "computed" },
  { id: "grossInvoiceValue", label: "Gross Invoice Value", visible: true, valueType: "computed", isNet: true },
  { id: "netPayableAmount", label: "Net Payable Amount", visible: true, valueType: "computed", isNet: true },
  { id: "amountWords", label: "Net Payable Amount (In Words)", visible: true, valueType: "words" },
];

export const DEFAULT_SELLER_FIELDS = [
  { id: "businessName", label: "Business Name", visible: true },
  { id: "registrationNo", label: "Registration No.", visible: true },
  { id: "strnNo", label: "STRN No.", visible: true },
  { id: "address", label: "Address", visible: true },
  { id: "province", label: "Province", visible: false },
];

export const DEFAULT_BUYER_FIELDS = [
  { id: "businessName", label: "Business Name", visible: true },
  { id: "registrationNo", label: "Registration No.", visible: true },
  { id: "strnNo", label: "STRN No.", visible: true },
  { id: "address", label: "Address", visible: true },
  { id: "province", label: "Province", visible: false },
];

export const DEFAULT_INVOICE_SUMMARY_FIELDS = [
  { id: "fbrInvoiceNo", label: "FBR Invoice No.", visible: true, side: "left" },
  { id: "sellerInvoiceNo", label: "Seller Invoice No.", visible: true, side: "left" },
  { id: "invoiceDate", label: "Invoice Date", visible: true, side: "right" },
  { id: "taxPeriod", label: "Tax Period", visible: true, side: "right" },
  { id: "invoiceType", label: "Invoice Type", visible: false, side: "left" },
  { id: "poNumber", label: "PO #", visible: true, side: "right" },
];

const normalizeInvoiceSummaryFields = (savedFields) => {
  const fields = Array.isArray(savedFields) ? savedFields : [];
  const defaultsById = new Map(DEFAULT_INVOICE_SUMMARY_FIELDS.map((field) => [field.id, field]));
  const seen = new Set();
  const normalized = [];

  fields.forEach((field) => {
    const defaultField = defaultsById.get(field?.id);
    if (!defaultField || seen.has(field.id)) return;
    seen.add(field.id);
    normalized.push({
      ...defaultField,
      visible: field.visible !== false,
      side: field.side === "left" || field.side === "right" ? field.side : defaultField.side,
    });
  });

  DEFAULT_INVOICE_SUMMARY_FIELDS.forEach((field) => {
    if (!seen.has(field.id)) normalized.push({ ...field });
  });

  return normalized;
};

export const defaultInvoiceDesignTemplate = {
  layoutVersion: 2,
  logo: "",
  letterhead: "",
  letterheadTop: 150,
  showDigitalInvoicing: true,
  showHeaderCompanyName: true,
  sellerContactEmail: "",
  sellerContactPhone: "",
  itemColumns: DEFAULT_ITEM_COLUMN_ORDER.map((key) => ({ key, visible: DEFAULT_ITEM_COLUMNS.includes(key) })),
  customItemColumns: [],
  breakdownRows: DEFAULT_BREAKDOWN_ROWS,
  sellerFields: DEFAULT_SELLER_FIELDS,
  buyerFields: DEFAULT_BUYER_FIELDS,
  invoiceSummaryFields: DEFAULT_INVOICE_SUMMARY_FIELDS,
  blocks: [
    { id: "company_logo", label: "Company Logo", visible: true, x: 214, y: 34, w: 132, h: 98 },
    { id: "seller_contact", label: "Seller Contact", visible: true, x: 24, y: 82, w: 130, h: 42 },
    { id: "invoice_title", label: "Invoice Title", visible: true, x: 0, y: 150, w: 560, h: 28 },
    { id: "invoice_meta", label: "Serial / Date / Time", visible: true, x: 24, y: 113, w: 512, h: 24 },
    { id: "invoice_summary", label: "Invoice Summary", visible: true, x: 35, y: 184, w: 490, h: 112 },
    { id: "seller_info", label: "Seller Information", visible: true, x: 35, y: 320, w: 230, h: 92 },
    { id: "buyer_info", label: "Buyer Information", visible: true, x: 300, y: 320, w: 230, h: 92 },
    { id: "description_code", label: "Description Code", visible: true, x: 365, y: 206, w: 170, h: 18 },
    { id: "items_table", label: "Items Table", visible: true, x: 26, y: 420, w: 508, h: 176 },
    { id: "fbr_invoice", label: "FBR Invoice Number", visible: true, x: 24, y: 296, w: 260, h: 18 },
    { id: "verification", label: "FBR Logo & QR Code", visible: true, x: 24, y: 354, w: 220, h: 92 },
    { id: "totals", label: "Total Breakdown", visible: true, x: 315, y: 616, w: 220, h: 190 },
    { id: "amount_words", label: "Amount in Words", visible: true, x: 315, y: 736, w: 220, h: 36 },
    { id: "signature", label: "Signature", visible: true, x: 24, y: 486, w: 280, h: 44 },
    { id: "invoice_note", label: "Invoice Note", visible: true, x: 26, y: 616, w: 280, h: 84 },
    { id: "additional_info", label: "Additional Information", visible: true, x: 26, y: 712, w: 280, h: 96 },
  ],
};

export const normalizeInvoiceDesignTemplate = (template = {}) => {
  const savedBlocks = Array.isArray(template.blocks) ? template.blocks : [];
  const savedColumns = Array.isArray(template.itemColumns) ? template.itemColumns : [];
  const knownColumnKeys = new Set(INVOICE_ITEM_FIELD_OPTIONS.map((field) => field.key));
  const seenColumnKeys = new Set();
  const itemColumns = [];
  savedColumns.forEach((column) => {
    if (!knownColumnKeys.has(column?.key) || seenColumnKeys.has(column.key)) return;
    seenColumnKeys.add(column.key);
    itemColumns.push({ key: column.key, visible: column.visible !== false });
  });
  DEFAULT_ITEM_COLUMN_ORDER.forEach((key) => {
    if (!seenColumnKeys.has(key)) itemColumns.push({ key, visible: DEFAULT_ITEM_COLUMNS.includes(key) });
  });
  const customItemColumns = Array.isArray(template.customItemColumns)
    ? template.customItemColumns
        .map((column, index) => ({
          id: column.id || `custom_item_${index + 1}`,
          label: column.label || `Custom ${index + 1}`,
          value: column.value || "",
          visible: column.visible !== false,
        }))
        .filter((column) => column.label.trim())
    : [];
  const savedBreakdownRows = Array.isArray(template.breakdownRows)
    ? template.breakdownRows.map((row) => (row?.id === "incomeTaxAdd" ? { ...row, id: "advanceIncomeTax" } : row))
    : [];
  const normalizeFields = (defaultFields, savedFields = []) => defaultFields.map((defaultField) => {
    const savedField = Array.isArray(savedFields) ? savedFields.find((field) => field?.id === defaultField.id) : null;
    return {
      ...defaultField,
      visible: savedField ? savedField.visible !== false : defaultField.visible !== false,
    };
  });
  const amountWordsBlock = savedBlocks.find((block) => block.id === "amount_words");
  const breakdownRows = DEFAULT_BREAKDOWN_ROWS.map((defaultRow) => {
    const savedRow = savedBreakdownRows.find((row) => row.id === defaultRow.id);
    if (defaultRow.id === "amountWords" && !savedRow && amountWordsBlock) {
      return { ...defaultRow, visible: amountWordsBlock.visible !== false };
    }
    return savedRow ? { ...defaultRow, visible: savedRow.visible !== false } : defaultRow;
  });
  const savedCustomBreakdownRows = savedBreakdownRows
    .filter((row) => !DEFAULT_BREAKDOWN_ROWS.some((defaultRow) => defaultRow.id === row.id))
    .map((row, index) => ({
      id: row.id || `custom_breakdown_${index + 1}`,
      label: row.label || `Custom ${index + 1}`,
      value: row.value || "",
      visible: row.visible !== false,
      valueType: "custom",
    }))
    .filter((row) => row.label.trim());
  const hasSavedInvoiceSummary = savedBlocks.some((block) => block.id === "invoice_summary");
  const modernDefaultBlockIds = new Set(["invoice_summary", "seller_info", "buyer_info", "items_table", "invoice_note", "additional_info", "totals", "amount_words"]);
  const blocks = defaultInvoiceDesignTemplate.blocks.map((defaultBlock) => {
    const savedBlock = savedBlocks.find((block) => block.id === defaultBlock.id);
    const shouldUseModernDefault = !hasSavedInvoiceSummary && modernDefaultBlockIds.has(defaultBlock.id);
    const mergedBlock = savedBlock && !shouldUseModernDefault ? { ...defaultBlock, ...savedBlock } : defaultBlock;
    if (mergedBlock.id === "company_logo") {
      const width = Math.max(Number(mergedBlock.w) || defaultBlock.w, defaultBlock.w);
      const height = Math.max(Number(mergedBlock.h) || defaultBlock.h, defaultBlock.h);
      const savedX = Number(savedBlock?.x);
      const savedY = Number(savedBlock?.y);
      const hasUserPosition = Boolean(savedBlock?.userPositioned) || (savedBlock && Number.isFinite(savedX) && savedX !== 42 && savedX !== 238);
      return {
        ...mergedBlock,
        w: width,
        h: height,
        x: hasUserPosition ? clampDesignValue(savedX, 0, DESIGN_CANVAS_WIDTH - width) : Math.round((DESIGN_CANVAS_WIDTH - width) / 2),
        y: hasUserPosition && Number.isFinite(savedY) ? clampDesignValue(savedY, 0, DESIGN_CANVAS_HEIGHT - height) : mergedBlock.y,
      };
    }
    if (mergedBlock.id === "invoice_title") {
      const hasLegacyTitlePosition = savedBlock && Number(savedBlock.x) === 24 && Number(savedBlock.y) === 76;
      if (hasLegacyTitlePosition) return defaultBlock;
      return {
        ...mergedBlock,
        w: Math.max(Number(mergedBlock.w) || 0, defaultBlock.w),
        h: Math.max(Number(mergedBlock.h) || 0, defaultBlock.h),
      };
    }
    return mergedBlock;
  });

  const normalized = {
    ...defaultInvoiceDesignTemplate,
    ...template,
    layoutVersion: 2,
    showDigitalInvoicing: template.showDigitalInvoicing !== false,
    showHeaderCompanyName: template.showHeaderCompanyName !== false,
    itemColumns,
    customItemColumns,
    breakdownRows: [...breakdownRows, ...savedCustomBreakdownRows],
    sellerFields: normalizeFields(DEFAULT_SELLER_FIELDS, template.sellerFields),
    buyerFields: normalizeFields(DEFAULT_BUYER_FIELDS, template.buyerFields),
    invoiceSummaryFields: normalizeInvoiceSummaryFields(template.invoiceSummaryFields),
    blocks,
  };

  const savedSummaryBlock = normalized.blocks.find((block) => block.id === "invoice_summary");
  const savedSellerBlock = normalized.blocks.find((block) => block.id === "seller_info");
  const savedBuyerBlock = normalized.blocks.find((block) => block.id === "buyer_info");
  const hasShortSummaryDefault =
    savedSummaryBlock?.x === 35 &&
    savedSummaryBlock?.y === 184 &&
    Number(savedSummaryBlock?.h) < 108 &&
    savedSellerBlock?.y === 300 &&
    savedBuyerBlock?.y === 300;

  if (hasShortSummaryDefault) {
    normalized.blocks = normalized.blocks.map((block) => {
      if (block.id === "invoice_summary") return { ...block, h: 112 };
      if (block.id === "seller_info" || block.id === "buyer_info") return { ...block, y: 320 };
      return block;
    });
  }

  const totalsBlock = normalized.blocks.find((block) => block.id === "totals");
  if (totalsBlock?.x === 315 && totalsBlock?.y === 616 && Number(totalsBlock?.h) < 186) {
    normalized.blocks = normalized.blocks.map((block) => (
      block.id === "totals" ? { ...block, h: 190 } : block
    ));
  }

  const additionalInfoBlock = normalized.blocks.find((block) => block.id === "additional_info");
  if (additionalInfoBlock?.x === 26 && additionalInfoBlock?.y === 712 && Number(additionalInfoBlock?.h) < 92) {
    normalized.blocks = normalized.blocks.map((block) => (
      block.id === "additional_info" ? { ...block, h: 96 } : block
    ));
  }

  const tableBlock = normalized.blocks.find((block) => block.id === "items_table");
  const legacyDefaultTable = tableBlock?.x === 24 && tableBlock?.y === 228 && tableBlock?.h === 118;
  if (legacyDefaultTable) {
    normalized.blocks = normalized.blocks.map((block) => {
      if (block.id === "items_table") return { ...block, h: 62 };
      if (["fbr_invoice", "verification", "totals", "signature"].includes(block.id)) {
        return { ...block, y: Math.max(0, block.y - 56) };
      }
      return block;
    });
  }

  return normalized;
};

export const getInvoiceSummaryLayout = (design, rows = []) => {
  const rowsById = new Map((Array.isArray(rows) ? rows : []).filter((row) => row?.id).map((row) => [row.id, row]));
  const nextRow = { left: 1, right: 1 };

  return normalizeInvoiceSummaryFields(design?.invoiceSummaryFields)
    .filter((field) => field.visible !== false && rowsById.has(field.id))
    .map((field) => ({
      ...rowsById.get(field.id),
      gridColumn: field.side === "right" ? "9 / span 4" : "1 / span 8",
      gridRow: nextRow[field.side]++,
      textAlign: field.side,
    }));
};

export const applySuggestedInvoiceLayout = (template) => {
  const normalized = normalizeInvoiceDesignTemplate(template);
  const columnsByKey = new Map(normalized.itemColumns.map((column) => [column.key, column]));
  const remainingColumns = normalized.itemColumns.filter((column) => !DEFAULT_ITEM_COLUMNS.includes(column.key));
  const summaryById = new Map(normalized.invoiceSummaryFields.map((field) => [field.id, field]));

  return {
    ...normalized,
    itemColumns: [...DEFAULT_ITEM_COLUMNS.map((key) => columnsByKey.get(key)), ...remainingColumns]
      .map((column) => (["discount", "lineTotal"].includes(column.key) ? { ...column, visible: false } : column)),
    sellerFields: normalized.sellerFields.map((field) => (field.id === "province" ? { ...field, visible: false } : field)),
    buyerFields: normalized.buyerFields.map((field) => (field.id === "province" ? { ...field, visible: false } : field)),
    invoiceSummaryFields: DEFAULT_INVOICE_SUMMARY_FIELDS.map((defaultField) => ({
      ...summaryById.get(defaultField.id),
      side: defaultField.side,
      ...(defaultField.id === "invoiceType" ? { visible: false } : {}),
    })),
  };
};

export const getInvoiceDesignStorageKey = (globalData) => {
  const clientId =
    globalData?.id ||
    globalData?.user_id ||
    globalData?.client_id ||
    globalData?.ntn_cnic ||
    globalData?.email;

  return clientId ? `${DESIGN_STORAGE_KEY}_${clientId}` : null;
};

export const loadLocalInvoiceDesign = (globalData) => {
  const key = getInvoiceDesignStorageKey(globalData);
  if (!key) return normalizeInvoiceDesignTemplate();

  try {
    const saved = JSON.parse(localStorage.getItem(key));
    if (saved?.blocks) return normalizeInvoiceDesignTemplate(saved);
  } catch {
    // An invalid user-specific cache must never fall back to another user's design.
  }

  return normalizeInvoiceDesignTemplate();
};

export const saveLocalInvoiceDesign = (template, globalData) => {
  const normalized = normalizeInvoiceDesignTemplate(template);
  const key = getInvoiceDesignStorageKey(globalData);
  if (key) localStorage.setItem(key, JSON.stringify(normalized));
  return normalized;
};

export const getDesignBlock = (design, id) => {
  const blocks = Array.isArray(design?.blocks) ? design.blocks : [];
  return blocks.find((block) => block.id === id);
};

export const toPrintMm = (value, axis = "x") => {
  const canvasSize = axis === "y" ? DESIGN_CANVAS_HEIGHT : DESIGN_CANVAS_WIDTH;
  const pageSize = axis === "y" ? PRINT_PAGE_HEIGHT_MM : PRINT_PAGE_WIDTH_MM;
  return `${(((Number(value) || 0) * pageSize) / canvasSize).toFixed(2)}mm`;
};
